// @flow
import { List, Map, Record } from "immutable"
import * as PickerFormComposer from "@kiwicom/frontend-app/scripts/composers/PickerFormComposer"

import { MODALS } from "mmb/consts/booking"
import * as bookingApi from "mmb/services/bookings/bookingsApi"
import { mapperCreatedBookingChange } from "../mapper/services/mapperManageBookingChange"
import { clearErrorAction, closeModal, openModal, reloadBooking } from "../index"
import type { FormFieldType } from "common/consts/form"
import { NEXT_FIELD } from "common/consts/form"
import SearchDate from "common/records/SearchDate"
import SearchPlace from "common/records/SearchPlace"
import * as PickerPricesComposer from "common/services/pickers/pickerPricesComposer"
import { currencySelector } from "common/services/currencies/selectors"
import type Journey from "common/records/Journey"
import type {
  Action,
  ChangeFlightsStateInst,
  ChangeFlightsStateType,
  CheckFlightAction,
  ClearAlternativeAction,
  ClearOriginalAction,
  ClearStateAction,
  ConfirmServiceForPayment,
  FieldsRecordType,
  FormRecordType,
  InitFromApiAction,
  OpenCloseFormAction,
  OpenContactFormAction,
  SelectAlternativeAction,
  SelectOriginalAction,
  SetActiveFieldAction,
  SetActiveModeAction,
  SetFormAction,
  SetNextFieldAction,
  SetValueAction,
  SubmitRecordType,
} from "./types"
import {
  CHECK_FLIGHT,
  CHECK_FLIGHT_FAILURE,
  CHECK_FLIGHT_REQUEST,
  CHECK_FLIGHT_SUCCESS,
  CLEAR_ALTERNATIVE,
  CLEAR_ORIGINAL,
  CLEAR_STATE,
  CONFIRM,
  CONFIRM_FAILURE,
  CONFIRM_REQUEST,
  CONFIRM_SUCCESS,
  FORM_ACTIVE_FIELD,
  FORM_ACTIVE_MODE,
  FORM_CHANGE_FORM,
  FORM_CHANGE_VALUE,
  FORM_NEXT_FIELD,
  FORM_SUBMIT,
  FORM_SUBMIT_FAILURE,
  FORM_SUBMIT_REQUEST,
  FORM_SUBMIT_SUCCESS,
  INITIATOR_API,
  SELECT_ALTERNATIVE,
  SELECT_ORIGINAL,
  SET_CONTACT_FORM_OPEN,
  SET_FORM_OPEN,
} from "mmb/scenes/AccountBooking/components/Booking/services/changeFlights/consts"

// Refactor to one "phase" with enum values
const SubmitRecord: SubmitRecordType = Record({
  success: false,
  loading: false,
  error: null,
})

const FieldsRecord: FieldsRecordType = Record({
  origin: new SearchPlace(),
  destination: new SearchPlace(),
  outboundDate: new SearchDate(),
  inboundDate: new SearchDate(),
  message: null,
})

const FormRecord: FormRecordType = Record({
  fields: new FieldsRecord(),
  activeModes: new PickerFormComposer.ActiveModesRecord(), // Active modes for all active fields
  activeField: null,
  prices: new PickerPricesComposer.PricesRecord(),
  submit: new SubmitRecord(),
})

const ChangeFlightsState: ChangeFlightsStateType = Record({
  selected: null, // selected journey/part to change
  alternatives: null, // available alternatives
  alternative: null, // desired one from alternatives
  initiatorApi: false, // @strajk rename to initialisedFromApi
  formOpen: true,
  contactFormOpen: false,
  checkFlight: new SubmitRecord(),
  confirmation: new SubmitRecord(),
  form: new FormRecord(),
})

function fillChangeFlightsState(map: Map): ChangeFlightsStateInst {
  return new ChangeFlightsState({
    ...map.toJS(),
    checkFlight: new SubmitRecord(map.get("checkFlight")),
    confirmation: new SubmitRecord(map.get("confirmation")),
    form: new FormRecord({
      ...map.get("form").toJS(),
      fields: new FieldsRecord({
        ...map.getIn(["form", "fields"]).toJS(),
        origin: new SearchPlace(map.getIn(["form", "fields", "origin"])),
        destination: new SearchPlace(map.getIn(["form", "fields", "destination"])),
        outboundDate: new SearchDate(map.getIn(["form", "fields", "outboundDate"])),
        inboundDate: new SearchDate(map.getIn(["form", "fields", "inboundDate"])),
      }),
      submit: new SubmitRecord(map.getIn(["form", "submit"])),
      prices: new PickerPricesComposer.PricesRecord(),
      activeModes: new PickerFormComposer.ActiveModesRecord(),
    }),
  })
}

export default function(state: ChangeFlightsState = new ChangeFlightsState(), action: Action) {
  if (!(state instanceof ChangeFlightsState)) return fillChangeFlightsState(state)

  switch (action.type) {
    case SELECT_ORIGINAL:
      return state.set("selected", action.journey)

    case CLEAR_ORIGINAL:
      return state.set("selected", null)

    case SELECT_ALTERNATIVE:
      return state.set("alternative", action.journey)

    case CLEAR_ALTERNATIVE:
      return state.set("alternative", null)

    case INITIATOR_API:
      return state
        .set("initiatorApi", true)
        .set("formOpen", false)
        .set("selected", action.selected)
        .set("alternatives", List(action.alternatives))

    case CLEAR_STATE:
      return new ChangeFlightsState()

    case SET_FORM_OPEN:
      return state.set("formOpen", action.payload)

    case SET_CONTACT_FORM_OPEN:
      return state.set("contactFormOpen", action.payload)

    // check flight
    // ===
    case CHECK_FLIGHT_REQUEST:
      return state
        .setIn(["checkFlight", "success"], false)
        .setIn(["checkFlight", "loading"], true)
        .setIn(["checkFlight", "error"], null)

    case CHECK_FLIGHT_SUCCESS:
      return state
        .setIn(["checkFlight", "success"], true)
        .setIn(["checkFlight", "loading"], false)
        .setIn(["checkFlight", "error"], null)

    case CHECK_FLIGHT_FAILURE:
      return state
        .set("alternative", null)
        .setIn(["checkFlight", "success"], false)
        .setIn(["checkFlight", "loading"], false)
        .setIn(["checkFlight", "error"], action.error)

    // confirmation
    // ===
    case CONFIRM_REQUEST:
      return state
        .setIn(["confirmation", "success"], false)
        .setIn(["confirmation", "loading"], true)
        .setIn(["confirmation", "error"], null)

    case CONFIRM_SUCCESS:
      return state
        .setIn(["confirmation", "success"], true)
        .setIn(["confirmation", "loading"], false)
        .setIn(["confirmation", "error"], null)

    case CONFIRM_FAILURE:
      return state
        .setIn(["confirmation", "success"], false)
        .setIn(["confirmation", "loading"], false)
        .setIn(["confirmation", "error"], action.error)

    // form
    // ===
    case FORM_ACTIVE_MODE:
      return state.setIn(["form", "activeModes", action.field], action.mode)

    case FORM_ACTIVE_FIELD:
      return state.setIn(["form", "activeField"], action.field)

    case FORM_NEXT_FIELD: {
      return state.setIn(["form", "activeField"], NEXT_FIELD[state.form.activeField])
    }

    case FORM_CHANGE_FORM:
      return state.setIn(["form", "fields"], new FieldsRecord(action.form))

    case FORM_CHANGE_VALUE:
      return state.setIn(
        ["form", "fields"],
        PickerFormComposer.handleFormChange(state.form.fields, action.value, action.field),
      )

    // submit
    // ---
    case FORM_SUBMIT_REQUEST:
      return state
        .setIn(["form", "submit", "success"], false)
        .setIn(["form", "submit", "loading"], true)
        .setIn(["form", "submit", "error"], null)

    case FORM_SUBMIT_SUCCESS:
      return state
        .setIn(["form", "submit", "success"], true)
        .setIn(["form", "submit", "loading"], false)
        .setIn(["form", "submit", "error"], null)

    case FORM_SUBMIT_FAILURE:
      return state
        .setIn(["form", "submit", "success"], false)
        .setIn(["form", "submit", "loading"], false)
        .setIn(["form", "submit", "error"], action.error)

    default:
      return state
  }
}

export function selectOriginal(journey: Journey): SelectOriginalAction {
  return {
    type: SELECT_ORIGINAL,
    journey,
    _track: { journey },
  }
}

export function clearOriginal(): ClearOriginalAction {
  return {
    type: CLEAR_ORIGINAL,
    _track: {},
  }
}

export function selectAlternative(journey: Journey): SelectAlternativeAction {
  return {
    type: SELECT_ALTERNATIVE,
    journey,
    _track: { journey },
  }
}

export function clearAlternative(): ClearAlternativeAction {
  return {
    type: CLEAR_ALTERNATIVE,
    _track: {},
  }
}

export function initFromApi(selected: Journey, alternatives: Array<Journey>): InitFromApiAction {
  return {
    type: INITIATOR_API,
    selected,
    alternatives,
    _track: { selected, alternatives },
  }
}

export function clearState(): ClearStateAction {
  return {
    type: CLEAR_STATE,
  }
}

export function openForm(): OpenCloseFormAction {
  return {
    type: SET_FORM_OPEN,
    payload: true,
  }
}

export function closeForm(): OpenCloseFormAction {
  return {
    type: SET_FORM_OPEN,
    payload: false,
  }
}

export function openContactForm(): OpenContactFormAction {
  return {
    type: SET_CONTACT_FORM_OPEN,
    payload: true,
  }
}

export function checkFlight(promise: Promise<*>): CheckFlightAction {
  return {
    type: CHECK_FLIGHT,
    promise,
    _track: {},
  }
}

// confirmation
// ===
export const confirm = (serviceForPayment: ConfirmServiceForPayment) => async (
  dispatch: Dispatch<*>,
  getState: () => any,
) => {
  const state = getState()
  const currency = currencySelector(state)

  const {
    payload: { booking, selected, alternative, useBalance },
  } = serviceForPayment

  const payload = {
    action: "create", // API nonsense
    bags: 0, // API nonsense
    flights: {
      booking_token: alternative.get("bookingToken"), // eslint-disable-line camelcase
      segment: bookingApi.detectSegment(booking, selected),
    },
    currency,
    payment: { gate: useBalance ? "credits" : "zooz" },
    use_credits: useBalance || false,
  }

  const promise = bookingApi.confirm(booking.id, payload)

  const ok = await dispatch({
    type: CONFIRM,
    action: CONFIRM,
    promise,
    _track: { ...payload, selected, alternative },
  })

  if (ok) {
    dispatch(clearState())

    if (serviceForPayment.price <= 0) {
      dispatch(reloadBooking(booking.id))
      dispatch(closeModal())
      return ok
    }

    return promise.then(res => res.detail).then(mapperCreatedBookingChange)
  }

  promise.catch(err => {
    if (err === "price difference too large") {
      dispatch(clearErrorAction()) // clear error state
      dispatch(clearState()) // clear flight change state
      dispatch(openModal(MODALS.FLIGHT_CHANGE_PRICE_ERROR))
    }
  })

  return ok
}

// form
// ===
export function setActiveMode(
  field: FormFieldType,
  mode: PickerFormComposer.ActiveModesRecord,
): SetActiveModeAction {
  return {
    type: FORM_ACTIVE_MODE,
    field,
    mode,
  }
}

export function setActiveField(field: FormFieldType): SetActiveFieldAction {
  return {
    type: FORM_ACTIVE_FIELD,
    field,
  }
}

export function setNextField(): SetNextFieldAction {
  return {
    type: FORM_NEXT_FIELD,
  }
}

export function setValue(field: FormFieldType, value: string): SetValueAction {
  return {
    type: FORM_CHANGE_VALUE,
    field,
    value,
  }
}

export function setForm(form: FieldsRecord): SetFormAction {
  return {
    type: FORM_CHANGE_FORM,
    form,
  }
}

export function submitForm(promise: Promise<*>) {
  return {
    type: FORM_SUBMIT,
    promise,
    _track: {},
  }
}
